/* eslint-disable sort-keys-fix/sort-keys-fix */
import Logo from './Logo';

// BASICS
export const baseColors = {
  black: '#312A25',
  gray: [
    '#fff', // 0 index
    '#f8f8f8',
    '#dedede',
    '#F4F1EE',
    '#979797',
    '#312A25',
  ],
  secondary: '#75AD40',
  secondaryHover: '#8BD04A',
  primary: '#731D05',
  tertiary: '#F4F1EE',
  tertiaryHover: '#B23C1B',
  red: '#f00101',
  white: '#fff',
  yellow: '#ffcc32',
};

export const colors = {
  ...baseColors,
  background: baseColors.white,
  danger: baseColors.red,
  dark: baseColors.gray[5],
  darkGray: baseColors.gray[5],
  primaryWhite: baseColors.white,
  info: baseColors.primary,
  light: baseColors.gray[3],
  lightGray: baseColors.gray[3],
  muted: baseColors.gray[1],
  success: baseColors.secondary,
  superLightGrey: baseColors.gray[1],
  text: baseColors.black,
  textMuted: baseColors.gray[4],
  warning: baseColors.yellow,
};

export const fonts = {
  body: "'Titillium Web Regular', sans-serif",
  heading: 'inherit',
};

export const fontWeights = {
  body: 400,
  bold: 700,
  display: 300,
  heading: 700,
  headingAlt: 600,
  light: 300,
  normal: 400,
};

export const fontSizes = {
  0: '1rem',
  1: '1.125rem',
  2: '1.25rem',
  3: '1.625rem',
  4: '1.875rem',
  5: '2rem',
  6: '2.1875rem',
  7: '2.5rem',
  8: '2.8125rem',
  9: '3.125rem',
};

export const lineHeights = {
  body: 1.5,
  heading: 1.5,
  paragraph: 1.75,
};

export const sizes = {
  lg: '1150px',
  md: '720px',
  sm: '540px',
  xl: '1440px',
};

export const radii = {
  default: '0.25rem',
  pill: '50rem',
  lg: '0.3rem',
  sm: '0.2rem',
};

export const shadows = {
  default: '0 .5rem 1rem rgba(0, 0, 0, .15)',
  lg: '0 1rem 3rem rgba(0, 0, 0, .175)',
  sm: '0 .125rem .25rem rgba(0, 0, 0, .075)',
};

const heading = {
  fontFamily: fonts.heading,
  fontWeight: fontWeights.heading,
  lineHeight: lineHeights.heading,
};

const display = {
  fontWeight: fontWeights.display,
  lineHeight: lineHeights.heading,
};

// variants
const typeStyles = {
  display,
  heading,
};

export const text = {
  caps: {
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
  },
  heading: {
    fontFamily: fonts.heading,
    fontWeight: fontWeights.heading,
    lineHeight: lineHeights.heading,
    textTransform: 'uppercase',
    color: colors.text,
  },
};

export const styles = {
  a: {
    '&:hover': {
      color: colors.secondary,
    },
    color: colors.primary,
    textDecoration: 'underline',
  },
  blockquote: {
    backgroundColor: colors.tertiary,
    color: colors.black,
    fontStyle: 'italic',
    fontWeight: 'normal',
    textTransform: 'normal',
  },
  h1: {
    ...heading,
    color: text.heading.color,
    fontSize: { desktop: fontSizes[9], mobile: fontSizes[7], tablet: fontSizes[8] },
    fontWeight: 600,
    lineHeight: { desktop: '3.5rem', mobile: '2.5rem', tablet: '3.125rem' },
  },
  h2: {
    ...heading,
    color: text.heading.color,
    fontSize: { desktop: fontSizes[7], mobile: fontSizes[5], tablet: fontSizes[6] },
    fontWeight: 600,
    lineHeight: { desktop: '2.6875rem', mobile: '2.25rem', tablet: '2.375rem' },
  },
  h3: {
    ...heading,
    color: text.heading.color,
    fontSize: { desktop: fontSizes[4], mobile: fontSizes[2], tablet: fontSizes[3] },
    fontWeight: 600,
    lineHeight: { desktop: '2.1875rem', mobile: '1.625rem', tablet: '1.875rem' },
  },
  h4: {
    ...heading,
    color: text.heading.color,
    fontSize: { desktop: fontSizes[2], mobile: fontSizes[1], tablet: fontSizes[1] },
    fontWeight: 600,
    lineHeight: { desktop: '1.5rem', mobile: '1.375rem', tablet: '1.375rem' },
  },
  h5: {
    ...heading,
    color: text.heading.color,
    fontSize: { desktop: fontSizes[0], mobile: fontSizes[0], tablet: fontSizes[0] },
    fontWeight: 600,
    lineHeight: { desktop: '1.25rem', mobile: '1.25rem', tablet: '1.25rem' },
  },
  highlight: {
    backgroundColor: colors.primary,
    color: colors.white,
  },
  img: {
    height: 'auto',
    maxWidth: '100%',
  },
  inlineCode: {
    color: colors.text,
  },
  li: {
    bulletBorderRadius: '10px',
    bulletColor: colors.primary,
  },
  p: {
    fontSize: { desktop: fontSizes[1], mobile: fontSizes[0], tablet: fontSizes[1] },
    fontWeight: 400,
    lineHeight: { desktop: '1.75rem', mobile: '1.625rem', tablet: '1.75rem' },
    margin: '0',
    marginBottom: '1.7rem',
  },
  root: {
    backgroundColor: colors.white,
    fontFamily: fonts.body,
    fontWeight: fontWeights.body,
    lineHeight: lineHeights.body,
  },
  rteHighlight: {
    color: colors.primary,
    hoverColor: colors.secondary,
    textDecoration: 'underline',
    isFooter: colors.white,
    fontWeight: 700,
  },
  table: {
    borderCollapse: 'collapse',
    color: colors.gray[4],
    marginBottom: '3rem',
    width: '100%',
  },
  td: {
    borderBottomColor: colors.gray[4],
    borderBottomStyle: 'solid',
    borderBottomWidth: 2,
    padding: '.75rem',
    verticalAlign: 'top',
  },
  th: {
    borderBottomColor: colors.gray[4],
    borderBottomStyle: 'solid',
    borderBottomWidth: '2px',
    borderTopColor: colors.gray[4],
    borderTopStyle: 'solid',
    borderTopWidth: '2px',
    padding: '.75rem',
    textAlign: 'inherit',
    verticalAlign: 'bottom',
  },
};

const buttonDefaults = {
  borderRadius: '0',
  cursor: 'pointer',
  fontFamily: fonts.body,
  fontSize: '14px',
  fontWeight: fontWeights.body,
  height: '48px',
  minWidth: '194px',
  transition:
    'background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), color 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
};

export const buttons = {
  disabled: {
    ...buttonDefaults,
    backgroundColor: colors.muted,
    color: colors.textMuted,
    cursor: 'default',
  },
  primary: {
    ...buttonDefaults,
    '&:hover': {
      backgroundColor: colors.secondaryHover,
      color: colors.text,
    },
    backgroundColor: colors.secondary,
    color: colors.text,
  },
  'primary-with-background': {
    ...buttonDefaults,
    '&:hover': {
      backgroundColor: colors.white,
      color: colors.tertiaryHover,
    },
    backgroundColor: colors.white,
    color: colors.primary,
  },
  secondary: {
    ...buttonDefaults,
    '&:hover': {
      backgroundColor: colors.primary,
      color: colors.white,
    },
    backgroundColor: colors.white,
    borderColor: colors.primary,
    borderStyle: 'solid',
    borderWidth: '1px',
    color: colors.primary,
  },
  tertiary: {
    ...buttonDefaults,
    '&:hover': {
      backgroundColor: colors.tertiary,
      color: colors.tertiaryHover,
    },
    backgroundColor: colors.tertiary,
    color: colors.primary,
  },
};

// COMPONENTS
export const adviceCardModule = {
  backgroundColor: colors.primary,
};

export const articleDetail = {
  introduction: colors.text,
  tags: {
    backgroundColor: colors.primary,
    color: colors.tertiary,
  },
  title: colors.text,
};

export const articleListing = {
  background: colors.primary,
  filter: colors.text,
  hover: colors.secondary,
  mobile: {
    background: colors.tertiary,
    title: colors.text,
    text: colors.text,
  },
  title: colors.text,
  text: colors.white,
};

export const breadcrumb = {
  backgroundColor: colors.tertiary,
  highlight: colors.primary,
};

export const cardCtas = {
  arrowColor: colors.primary,
  arrowHoverColor: colors.primary,
  color: colors.primary,
  fontSize: '1rem',
  hoverColor: colors.tertiaryHover,
  letterSpacing: '2px',
  lineHeight: '14px',
  textTransform: 'uppercase',
};

export const cards = {
  article: {
    description: colors.text,
    link: {
      '&:hover': {
        color: colors.secondary,
        opacity: 1,
        textDecoration: 'underline',
      },
      color: colors.primary,
    },
    title: colors.text,
  },
  logo: {
    color: colors.text,
  },
};

export const carousel = {
  controls: {
    hero: {
      backgroundColor: colors.white,
      stroke: colors.white,
      color: colors.primary,
      opacity: '0.8',
      hover: {
        backgroundColor: colors.primary,
        color: colors.white,
        stroke: colors.primary,
      },
    },
    regular: {
      backgroundColor: colors.white,
      color: colors.primary,
      hover: {
        color: colors.primary,
        backgroundColor: colors.tertiary,
      },
    },
  },
  indicator: colors.primary,
  link: {
    '&:hover': {
      color: colors.secondary,
      opacity: 1,
      textDecoration: 'underline',
    },
    color: colors.primary,
  },
  text: colors.text,
};

export const categoryCards = {
  backgroundColor: colors.primary,
};

export const categorySection = {
  background: colors.primary,
  boxShadow: null,
  chevron: {
    fill: colors.white,
    stroke: colors.primary,
  },
  color: colors.white,
  date: colors.primary,
  dateColor: colors.white,
  fontWeight: 700,
  headingColor: colors.text,
};

export const columns = {
  imageCard: colors.text,
  imageCardTitle: colors.text,
  infoBlock: {
    background: colors.tertiary,
    description: colors.text,
    title: colors.text,
    titleFontWeight: 400,
  },
};

export const contactForm = {
  heading: {
    color: colors.primary,
  },
};

export const contacts = {
  office: {
    mapTextColor: colors.primary,
    mapTextFontSize: '0.875rem',
    mapTextFontWeight: 'bold',
    mapTextLetterSpacing: '2.33px',
    mapTextLineHeight: '1.71',
    mapTextTransform: 'uppercase',
    openingHoursFontSize: '1rem',
  },
};

export const ctaBanner = {
  focusImage: {
    backgroundColor: colors.primary,
    titleColor: colors.white,
    color: colors.white,
  },
  focusText: {
    backgroundColor: colors.tertiary,
    titleColor: colors.text,
    titleFontWeight: 600,
    color: colors.text,
  },
};

export const ctaGroup = {
  title: colors.text,
  chevron: {
    opacity: 1,
    stroke: colors.secondary,
  },
  borderRadius: 0,
};

export const customBlockCards = {
  backgroundColor: colors.primary,
  color: colors.primary,
};

export const dealer = {
  textColor: colors.primary,
};

export const documentDownload = {
  arrowColor: colors.primary,
  arrowHoverColor: colors.primary,
  color: colors.primary,
  hoverColor: colors.primary,
};

export const footer = {
  activeColor: colors.secondary,
  backgroundColor: colors.white,
  color: colors.white,
  minHeight: '160px',
};

export const formInputs = {
  activeColor: colors.secondary,
  borderRadius: '0',
  radioInputBorderRadius: '50%',
  radioSpanBorderRadius: '50%',
};

export const header = {
  activeColor: colors.primary,
  backgroundColor: colors.white,
  color: colors.text,
};

export const headerBottom = {
  activeColor: colors.secondary,
  activeFontWeight: '400',
  backgroundColor: colors.primary,
  color: colors.white,
  fontSize: '12px',
  fontWeight: '400',
};

export const heroBannerButton = {
  'No Background': {
    '&:hover': {
      backgroundColor: colors.white,
      borderColor: colors.white,
      color: colors.primary,
    },
    backgroundColor: colors.white,
    borderColor: colors.white,
    borderStyle: 'solid',
    borderWidth: '2px',
    color: colors.secondary,
    height: '48px',
    justifyContent: 'center',
  },
  'White Background': {
    '&:hover': {
      backgroundColor: colors.tertiaryHover,
      borderColor: colors.tertiaryHover,
      color: colors.white,
    },
    backgroundColor: colors.white,
    borderColor: colors.primary,
    borderStyle: 'solid',
    borderWidth: '2px',
    color: colors.primary,
    justifyContent: 'center',
    minHeight: '48px',
    padding: '13px 35px 12px',
  },
  arrow: {
    display: 'none',
  },
};

export const heroSection = {
  titleFontWeight: 'normal',
};

export const imageAndText = {
  color: colors.primary,
};

export const infoBlock = {
  background: colors.tertiary,
  description: colors.text,
  title: colors.text,
};

export const infoSection = {
  backgroundColor: colors.primary,
  color: colors.white,
  title: colors.white,
};

export const ingredients = {
  backgroundColor: colors.primary,
  color: colors.white,
  hoverColor: colors.secondary,
};

export const media = {
  backgroundColor: colors.primary,
  titleFontWeight: 'normal',
  titleTextTransform: 'normal',
};

export const mobileNavigation = {
  arrowColor: colors.lightGray,
  chevronColor: colors.text,
};

export const navItems = {
  fontSize: '14px',
  fontWeight: 400,
  letterSpacing: '1px',
  selectedFontWeight: 400,
};

export const newsListing = {
  titleFontWeight: 'normal',
};

export const ownerDetail = {
  introduction: colors.text,
  tags: {
    backgroundColor: colors.primary,
    color: colors.tertiary,
  },
  title: colors.text,
};

export const product = {
  backgroundColor: colors.secondary,
  color: colors.white,
};

export const productListing = {
  titleFontWeight: 'normal',
};

export const salesPerson = {
  borderColor: colors.primary,
};

export const search = {
  activeColor: colors.primary,
  borderRadius: '0',
  fontWeight: 'normal',
};

export const shortBanner = {
  backgroundColor: colors.primary,
  color: colors.white,
  titleColor: colors.white,
  titleFontWeight: 'normal',
  titleTextTransform: 'none',
};

export const table = {
  headerBackgroundColor: colors.primary,
  headerColor: colors.white,
  headerFontSize: '14px',
  headerLetterSpacing: '2px',
  scrollbarColor: colors.primary,
};

export const upComingEvents = {
  backgroundColor: colors.primary,
  color: colors.white,
};

export default {
  adviceCardModule,
  articleDetail,
  articleListing,
  breadcrumb,
  buttons,
  cardCtas,
  cards,
  carousel,
  categoryCards,
  categorySection,
  colors,
  columns,
  contactForm,
  contacts,
  ctaBanner,
  ctaGroup,
  customBlockCards,
  dealer,
  documentDownload,
  fonts,
  fontSizes,
  fontWeights,
  footer,
  formInputs,
  header,
  headerBottom,
  heroBannerButton,
  heroSection,
  imageAndText,
  infoBlock,
  infoSection,
  ingredients,
  lineHeights,
  logo: Logo,
  media,
  mobileNavigation,
  name: 'Hobbyfirst',
  navItems,
  newsListing,
  ownerDetail,
  product,
  productListing,
  radii,
  salesPerson,
  search,
  shadows,
  shortBanner,
  sizes,
  styles,
  table,
  text,
  typeStyles,
  upComingEvents,
};
